import { useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { ArrowDownNarrowWide, ArrowUpNarrowWide, ArrowUpRight, CalendarDays, MessageSquare, MessagesSquare, Star, StarOff } from "lucide-react";
import { api, type Post, type ThreadMeta } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { PriceAtTime } from "@/components/PriceAtTime";
import { RefreshButton } from "@/components/RefreshButton";
import { SummarizeButton } from "@/components/SummarizeButton";
import { usePathTab } from "@/lib/usePathTab";
import { useSeo } from "@/lib/useSeo";
import { useRequireAuth } from "@/hooks/useAuth";
import { useToggleFollow } from "@/hooks/useToggleFollow";
import { fromNowFR, formatNumber, cn } from "@/lib/utils";

const ORDERS = ["asc", "desc"] as const;
type Order = (typeof ORDERS)[number];

const FR_DATE = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function fmtFull(s: string): string {
  const d = new Date(s);
  if (isNaN(d.getTime()) || d.getFullYear() < 2000) return "—";
  return FR_DATE.format(d);
}

// Tooltip content shown when hovering a pseudo : account age + activity.
// member_since = real Boursorama account creation ("membre depuis…").
// Falls back to first_seen_at (our first scrape) prefixed with "vu depuis"
// so we never imply a creation date we don't actually know.
function AuthorTooltip({ post }: { post: Post }) {
  const hasMember = !!post.author_member_since;
  const ageDate = post.author_member_since ?? post.author_first_seen_at;
  return (
    <div className="space-y-1 text-xs">
      <div className="flex items-center gap-1.5">
        <CalendarDays className="size-3.5 shrink-0" />
        {ageDate ? (
          <span>
            {hasMember ? "Membre depuis" : "Vu depuis"} {fromNowFR(ageDate)}
          </span>
        ) : (
          <span className="text-foreground-muted">Ancienneté inconnue</span>
        )}
      </div>
      {post.author_posts_count != null && (
        <div className="flex items-center gap-1.5">
          <MessageSquare className="size-3.5 shrink-0" />
          <span>{formatNumber(post.author_posts_count)} messages</span>
        </div>
      )}
      {post.author_stars != null && post.author_stars > 0 && (
        <div className="flex items-center gap-1.5">
          <Star className="size-3.5 shrink-0" />
          <span>{post.author_stars} BoursoStars</span>
        </div>
      )}
    </div>
  );
}

export default function ThreadDetailPage() {
  const { id } = useParams();
  const [order, setOrder] = usePathTab<Order>("order", `/threads/${id}`, "asc", ORDERS);
  const gate = useRequireAuth();

  const { data = [], isLoading, isError, refetch } = useQuery<Post[]>({
    queryKey: ["posts", id],
    queryFn: () => api.posts(id!),
    enabled: !!id,
  });

  // Thread metadata (real title + Boursorama link + owning forum). Lightweight,
  // separately cached — the posts list stays a pure Post[].
  const { data: meta } = useQuery<ThreadMeta>({
    queryKey: ["thread", id],
    queryFn: () => api.thread(id!),
    enabled: !!id,
    retry: false,
    staleTime: 60_000,
  });

  // Title = real thread title + owning forum once metadata lands (tab label,
  // sharing, GA page_title).
  //
  // noindex: this page renders the raw post bodies scraped from Boursorama —
  // republishing them verbatim is duplicate content (Boursorama is the
  // canonical source). Keeping it out of the index protects the whole site
  // from a scraper-content penalty. The page stays fully usable for logged-in
  // users; we just don't want Google to rank our copy of their text.
  useSeo({
    title: meta?.title
      ? `${meta.title}${meta.forum_title ? ` — forum ${meta.forum_title}` : ""}`
      : "Discussion — Boursicote",
    canonicalPath: `/threads/${id}`,
    noindex: true,
  });

  // Follow/unfollow this thread (alerts on new replies). Same optimistic
  // pattern as forums/users — the backend supported target_type "thread" all
  // along, the button just didn't exist.
  const { data: follows = [] } = useQuery({
    queryKey: ["follows"],
    queryFn: () => api.follows(),
    retry: false,
  });
  const threadId = Number(id);
  const isFollowed = useMemo(
    () => follows.some((f) => f.target_type === "thread" && Number(f.target_id) === threadId),
    [follows, threadId]
  );
  const toggleFollow = useToggleFollow("thread");

  // Backend returns posts sorted ASC by posted_at. We sort client-side when
  // the user flips the order, so the natural reading order (oldest first) is
  // preserved by default.
  const sorted = useMemo(() => {
    if (data.length === 0) return data;
    if (order === "desc") return [...data].reverse();
    return data;
  }, [data, order]);

  const lastPost = useMemo(() => {
    if (data.length === 0) return null;
    // The "last" message is always the most recent, regardless of UI order.
    return data.reduce<Post>((acc, p) => {
      const a = new Date(acc.posted_at).getTime();
      const b = new Date(p.posted_at).getTime();
      return b > a ? p : acc;
    }, data[0]);
  }, [data]);

  const lastPostRef = useRef<HTMLDivElement | null>(null);
  function scrollToLast() {
    const node = lastPostRef.current;
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      node.classList.add("ring-2", "ring-primary/60");
      setTimeout(() => node.classList.remove("ring-2", "ring-primary/60"), 1800);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex flex-col gap-1.5 min-w-0 flex-1">
          {/* Eyebrow : owning forum crumb. Encodes where this conversation
              lives — clickable back to the forum's thread list. */}
          {meta?.forum_title && (
            <Link
              to={`/forums/${meta.forum_id}/threads`}
              className="inline-flex w-fit items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-foreground-muted hover:text-primary transition-colors"
            >
              <MessagesSquare className="size-3.5" />
              {meta.forum_title}
            </Link>
          )}
          {/* Hero : the real thread title (was "Posts"). */}
          <h1 className="text-2xl font-semibold leading-tight break-words">
            {meta?.title || "Discussion"}
          </h1>
          <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-sm text-foreground-muted">
            <span className="tabular-nums">{formatNumber(data.length)} message(s)</span>
            {lastPost && (
              <>
                <span aria-hidden>·</span>
                <span>
                  dernier <strong className="text-foreground">{fromNowFR(lastPost.posted_at)}</strong>
                  <span className="opacity-70"> ({fmtFull(lastPost.posted_at)})</span>
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={scrollToLast}
                  title="Aller au dernier message"
                  className="h-6 px-2 -my-1"
                >
                  <ArrowUpRight className="size-3.5" />
                  Y aller
                </Button>
              </>
            )}
            {meta?.url && (
              <a
                href={meta.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                Voir sur Boursorama <ArrowUpRight className="size-3" />
              </a>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {id && (
            <Button
              size="sm"
              variant={isFollowed ? "default" : "outline"}
              onClick={() =>
                gate(
                  () => toggleFollow.mutate({ id: threadId, label: meta?.title || `Sujet #${threadId}` }),
                  { actionLabel: "suivre un sujet" }
                )
              }
              title={
                isFollowed
                  ? "Ne plus suivre ce sujet"
                  : "Être alerté des nouvelles réponses sur ce sujet"
              }
            >
              {isFollowed ? <Star className="size-3.5" /> : <StarOff className="size-3.5" />}
              {isFollowed ? "Suivi" : "Suivre ce sujet"}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOrder(order === "asc" ? "desc" : "asc")}
            title={
              order === "asc"
                ? "Trier du plus récent au plus ancien"
                : "Trier du plus ancien au plus récent"
            }
          >
            {order === "asc" ? (
              <>
                <ArrowUpNarrowWide className="size-3.5" />
                Plus ancien en premier
              </>
            ) : (
              <>
                <ArrowDownNarrowWide className="size-3.5" />
                Plus récent en premier
              </>
            )}
          </Button>
          {id && (
            <RefreshButton
              kind="thread"
              id={id}
              label="Re-scraper ce thread"
              invalidate={[["posts", id]]}
            />
          )}
        </div>
      </header>

      {id && data.length > 0 && <SummarizeButton kind="thread" id={id} label="Résumer le sujet" />}

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : isError ? (
        <Card>
          <CardContent className="py-10 text-center text-sm flex flex-col items-center gap-3">
            <span className="text-foreground-muted">
              Impossible de charger les posts (erreur réseau).
            </span>
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              Réessayer
            </Button>
          </CardContent>
        </Card>
      ) : data.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-foreground-muted">
            Aucun post pour ce thread.<br />
            Cliquez sur « Re-scraper ce thread » pour déclencher un scrape immédiat.
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {sorted.map((p) => {
            const isLast = lastPost?.id === p.id;
            return (
              <Card
                key={p.id}
                ref={isLast ? lastPostRef : undefined}
                className={cn(
                  "p-4 transition-colors",
                  isLast &&
                    "relative overflow-hidden border-primary before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-primary"
                )}
              >
                <div className="flex items-baseline justify-between gap-2">
                  {p.author_id ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Link
                          to={`/users/${p.author_id}`}
                          className="font-semibold hover:underline"
                        >
                          {p.username || "?"}
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent>
                        <AuthorTooltip post={p} />
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="font-semibold text-foreground-muted italic">
                      {p.username || "Profil supprimé"}
                    </span>
                  )}
                  <div className="flex items-center gap-1.5 text-xs text-foreground-muted">
                    <PriceAtTime
                      price={p.price_at_post}
                      variation={p.variation_at_post}
                      currency={p.price_currency}
                      source={p.price_source}
                    />
                    {isLast && (
                      <span className="rounded-md bg-primary/15 text-primary px-1.5 py-0.5 text-[10px] font-medium">
                        dernier
                      </span>
                    )}
                    <span title={fmtFull(p.posted_at)}>{fromNowFR(p.posted_at)}</span>
                  </div>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed">{p.content}</p>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
